import React, { useEffect, useRef } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import styles from '../_pages/PreviousImages/v2/PreviousImages.module.scss';
import Spinner from '../Spinner';
import PastImageItems from './PastImageItems';
import usePastImages from '../../hooks/usePastImages';
import usePastImagesPricelists from '../../hooks/usePastImagesPricelists';

export default function PastImagesContent({
  onContinue, onEmpty, student, query = {},
}) {
  const headerText = `Complete your collection of ${student.firstName}'s school pictures.`;

  // Same data PastImageItems renders from (react-query dedupes the fetches): a
  // past image only DISPLAYS when a past-images pricelist matches its season, so
  // "nothing will render" is decidable here, before the sales pitch paints.
  const { isFetched: imagesFetched, data: pastImages } = usePastImages({ studentId: student.id, additionalQuery: query });
  const { isFetched: pricelistsFetched, data: pricelists } = usePastImagesPricelists();
  const displayableCount = (pastImages || []).filter(
    (studentImage) => (pricelists || []).some((p) => studentImage.folder.includes(p.season)),
  ).length;

  // `isFetched`, NOT `isLoading`. Both queries declare `placeholderData: []`, and react-query
  // reports a placeholder as SUCCESS — so `isLoading` is already false on the very first
  // render, while both arrays are still empty. Reading it as "the answer is in" made
  // `displayableCount === 0` look like a verdict instead of a starting value, and the effect
  // below fired onEmpty on mount: every customer arriving with a prior-season access code was
  // bounced to an empty checkout before their own images had loaded. `isFetched` flips only
  // when a real fetch resolves.
  //
  // The render path below never showed this because `useIsFetching()` held a spinner over it.
  // The EFFECT ran anyway — hooks run before that early return — so the page redirected while
  // apparently still loading.
  //
  // No student id disables the images query, which then never fetches: treat that as settled
  // so the caller still decides where to go, rather than parking on a pitch that can't fill.
  const settled = !student.id || (imagesFetched && pricelistsFetched);

  // A student with NO sellable past images must not see the pitch at all — the
  // caller decides where to go instead (e.g. straight to checkout). Fired once.
  const emptyFiredRef = useRef(false);
  useEffect(() => {
    if (!settled || displayableCount > 0 || !onEmpty) return;
    if (emptyFiredRef.current) return;
    emptyFiredRef.current = true;
    onEmpty();
  }, [settled, displayableCount, onEmpty]);

  const isFetching = useIsFetching();

  if (isFetching) return <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%)' }}><Spinner color='blue' /></div>;
  if (settled && displayableCount === 0) return null;

  return (
    <>
      <h1 className={styles.header}>
        <span>Don't miss a single memory</span>
      </h1>
      <p className={styles.saleblue}>SAVE ON PICTURES FROM PREVIOUS SCHOOL YEARS!</p>
      <h2 className={styles.sale}><span>{headerText}</span></h2>


      <div className={styles.grid}>
        <PastImageItems
          student={student}
          onContinue={onContinue}
          query={query}
        />
      </div>
    </>
  );
}
